import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CaretLeft, CaretRight, WarningCircle, Warning, Gavel, CheckCircle } from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/features/auth/store';
import { todayKey } from '@/lib/diaryStore';
import { fetchOperationsDiary, fetchDiaryIntelligence, type DiaryItem, type DiaryWarning } from '@/lib/operationsApi';

/**
 * Chambers Operations Board — chambers-wide operational status. Presentation only: aggregates the
 * existing /operations/diary (scope=chambers) + intelligence into status buckets. No new engine,
 * no new API, no duplicated calculation — lifecycle/readiness/obligations are canonical values.
 */
export default function ChambersOperationsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const basePath = isClerk ? '/clerk' : '/admin';
  const [day, setDay] = useState<string>(() => todayKey());

  const from = `${day}T00:00:00.000Z`;
  const to = `${day}T23:59:59.999Z`;

  const diaryQ = useQuery({ queryKey: ['ops-board', day], queryFn: () => fetchOperationsDiary({ scope: 'chambers', from, to }), staleTime: 60_000 });
  const intelQ = useQuery({ queryKey: ['ops-board-intel', day], queryFn: () => fetchDiaryIntelligence({ scope: 'chambers', from, to }), staleTime: 60_000 });

  const items = diaryQ.data ?? [];
  const warnings = intelQ.data ?? [];

  const b = useMemo(() => buckets(items), [items]);
  const act = warnings.filter((w) => w.severity === 'act');
  const goItem = (it: DiaryItem) => it.hearing ? navigate(`${basePath}/hearings/${it.hearing.id}`) : it.matter ? navigate(`${basePath}/matters/${it.matter.id}`) : undefined;
  const goWarn = (w: DiaryWarning) => w.subjectType === 'hearing' ? navigate(`${basePath}/hearings/${w.subjectId}`) : w.subjectType === 'matter' ? navigate(`${basePath}/matters/${w.subjectId}`) : undefined;

  const shiftDay = (n: number) => { const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); setDay(d.toISOString().slice(0, 10)); };

  return (
    // px-1 py-2 dropped with the hand-rolled header: it inset this page's content 4px from
    // where every other page's sits, which is the kind of difference you feel without being
    // able to name it.
    <div className="space-y-5">
      {/* PageHeader rather than a hand-rolled header, so this page carries the same back
          control and title scale as the rest. Its title was text-xl where every other page
          is text-2xl, and the leading icon pushed it out of line with them. The day picker
          rides along as the header's action. */}
      <PageHeader
        onBack={() => navigate(-1)}
        title="Operations Board"
        subtitle="Chambers-wide operational status"
        actions={
          <div className="flex items-center gap-1">
            <button onClick={() => shiftDay(-1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-muted" aria-label="Previous day"><CaretLeft className="h-4 w-4" /></button>
            <button onClick={() => setDay(todayKey())} className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted">Today</button>
            <button onClick={() => shiftDay(1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-border hover:bg-muted" aria-label="Next day"><CaretRight className="h-4 w-4" /></button>
            {/* ml-auto below sm: on a phone the action group is given the whole line, so the
                date sat in the middle of an empty row right next to the arrows. Pushed to the
                far right it reads as the row's label and the controls keep their own corner.
                From sm up the group is content-width and right-aligned already, so the plain
                8px gap is restored. */}
            <span className="ml-auto whitespace-nowrap text-sm font-medium text-foreground sm:ml-2">{new Date(`${day}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
          </div>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        <Kpi label="In court" value={b.inCourt.length} tone="brand" />
        <Kpi label="Awaiting outcome" value={b.awaiting.length} tone="amber" />
        <Kpi label="Blocked" value={b.blocked.length} tone="red" />
        <Kpi label="Unprepared" value={b.unprepared.length} tone="amber" />
        <Kpi label="Completed" value={b.completed.length} tone="emerald" />
        <Kpi label="Fees due" value={b.feesDue.length} tone="amber" />
      </div>

      {diaryQ.isLoading && <div className="text-sm text-muted-foreground">Loading operations…</div>}
      {diaryQ.isError && <div className="text-sm text-danger">Couldn&apos;t load. <button className="underline" onClick={() => diaryQ.refetch()}>Retry</button></div>}

      {/* Needs intervention */}
      {(act.length > 0 || b.uncovered.length > 0) && (
        <Section title="Needs intervention" tone="red" icon={<WarningCircle className="h-4 w-4" weight="fill" />}>
          {b.uncovered.map((it) => <RowItem key={it.id} onClick={() => goItem(it)} left={<Gavel className="h-4 w-4 text-red-600 dark:text-red-400" />} text={`Uncovered — ${label(it)}`} />)}
          {act.map((w, i) => <RowItem key={`a${i}`} onClick={() => goWarn(w)} left={<Warning className="h-4 w-4 text-red-600 dark:text-red-400" weight="fill" />} text={w.detail} />)}
        </Section>
      )}

      {b.blocked.length > 0 && <Bucket title="Blocked" tone="red" items={b.blocked} onItem={goItem} badge={(it) => <Badge variant="danger">{it.obligations!.blocked} blocked</Badge>} />}
      {b.unprepared.length > 0 && <Bucket title="Unprepared" tone="amber" items={b.unprepared} onItem={goItem} badge={(it) => <Badge variant="warning">{it.readiness!.percentage}%</Badge>} />}
      {b.awaiting.length > 0 && <Bucket title="Awaiting outcome" tone="amber" items={b.awaiting} onItem={goItem} badge={() => <Badge variant="warning">outcome due</Badge>} />}
      {b.inCourt.length > 0 && <Bucket title="In court" tone="brand" items={b.inCourt} onItem={goItem} badge={() => <Badge variant="info">in court</Badge>} />}

      {!diaryQ.isLoading && items.length === 0 && (
        <Card><CardContent className="flex flex-col items-center gap-2 py-14 text-center">
          <CheckCircle className="h-9 w-9 text-emerald-500/40" weight="duotone" />
          <div className="text-sm font-semibold text-foreground">All quiet</div>
          <p className="text-xs text-muted-foreground">Nothing operationally active today.</p>
        </CardContent></Card>
      )}
    </div>
  );
}

/* ── buckets (derived from canonical DiaryItem values) ── */
const COMPLETED = ['Outcome recorded', 'Done', 'Billing pending'];
const UPCOMING = ['Scheduled', 'Preparing', 'Ready'];
function buckets(items: DiaryItem[]) {
  const inCourt = items.filter((i) => i.lifecycle === 'In court');
  const awaiting = items.filter((i) => i.lifecycle === 'Awaiting outcome');
  const blocked = items.filter((i) => (i.obligations?.blocked ?? 0) > 0);
  const unprepared = items.filter((i) => i.hearing && i.readiness && i.readiness.percentage < 50 && UPCOMING.includes(i.lifecycle));
  const completed = items.filter((i) => COMPLETED.includes(i.lifecycle));
  const feesDue = items.filter((i) => i.money?.outstanding);
  const uncovered = items.filter((i) => !i.owner && i.hearing);
  return { inCourt, awaiting, blocked, unprepared, completed, feesDue, uncovered };
}
const label = (it: DiaryItem) => it.matter ? `${it.matter.number} · ${it.matter.name}` : it.title;

function Kpi({ label, value, tone }: { label: string; value: number; tone: string }) {
  const c = { brand: 'text-brand-700 dark:text-brand-300', amber: 'text-amber-700 dark:text-amber-300', red: 'text-red-700 dark:text-red-300', emerald: 'text-emerald-700 dark:text-emerald-300' }[tone] ?? 'text-foreground';
  return (
    <div className="rounded-xl border border-border bg-card px-3 py-2.5 text-center">
      <div className={`text-2xl font-bold tabular-nums ${value > 0 ? c : 'text-muted-foreground/50'}`}>{value}</div>
      <div className="text-[10px] font-medium text-muted-foreground">{label}</div>
    </div>
  );
}
function Section({ title, tone, icon, children }: { title: string; tone: string; icon: React.ReactNode; children: React.ReactNode }) {
  const border = tone === 'red' ? 'border-l-red-400' : tone === 'amber' ? 'border-l-amber-400' : 'border-l-brand-400';
  const text = tone === 'red' ? 'text-red-700 dark:text-red-300' : tone === 'amber' ? 'text-amber-700 dark:text-amber-300' : 'text-brand-700 dark:text-brand-300';
  return (
    <Card className={`border-l-4 ${border}`}>
      <CardContent className="space-y-1 p-4">
        <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider ${text}`}>{icon} {title}</div>
        {children}
      </CardContent>
    </Card>
  );
}
function RowItem({ onClick, left, text }: { onClick?: () => void; left: React.ReactNode; text: string }) {
  return (
    <div onClick={onClick} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs text-foreground hover:bg-muted/40">
      <span className="shrink-0">{left}</span><span className="flex-1 truncate">{text}</span>
    </div>
  );
}
function Bucket({ title, tone, items, onItem, badge }: { title: string; tone: string; items: DiaryItem[]; onItem: (it: DiaryItem) => void; badge: (it: DiaryItem) => React.ReactNode }) {
  const text = tone === 'red' ? 'text-red-700 dark:text-red-300' : tone === 'amber' ? 'text-amber-700 dark:text-amber-300' : 'text-brand-700 dark:text-brand-300';
  return (
    <Card>
      <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
        <span className={`text-sm font-semibold ${text}`}>{title}</span><Badge variant="outline">{items.length}</Badge>
      </div>
      <CardContent className="p-0">
        {items.sort((a, c) => a.startAt.localeCompare(c.startAt)).map((it) => (
          <button key={it.id} onClick={() => onItem(it)} className="flex w-full items-center gap-3 border-b border-border/40 px-4 py-2.5 text-left last:border-0 hover:bg-muted/30">
            <span className="w-11 shrink-0 text-sm font-semibold tabular-nums text-foreground">{it.startAt.slice(11, 16)}</span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-foreground">{label(it)}</div>
              <div className="truncate text-[11px] capitalize text-muted-foreground">{[it.hearing?.type, it.owner?.name, it.matter?.court].filter(Boolean).join(' · ')}</div>
            </div>
            {badge(it)}
          </button>
        ))}
      </CardContent>
    </Card>
  );
}
